import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Grid,
  Heart,
  Tag,
  CheckCircle,
  MessageCircle,
  UserPlus,
  UserCheck,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { SocialPostImage } from './SocialPostImage';
import { AvatarDisplay } from './AvatarDisplay';
import { PostDetailModal } from './PostDetailModal';
import type { SocialPost } from './socialTypes';
import * as socialApi from '@/api/social';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useSocialFeed } from '../../context/SocialFeedContext';

interface OtherPetProfileProps {
  profileData: {
    petName: string;
    ownerName: string;
    /** Owner (user) avatar – shown as main profile photo when viewing another user's profile */
    petAvatar: string;
    ownerAvatar?: string;
    professionalBadge?: string;
    ownerId?: string;
    petId?: string;
    trendingScore?: number;
  };
  onBack: () => void;
  onMessage: (userId: string, userName: string, avatar?: string | null) => void;
  /** Called after a follow/unfollow so the parent can refresh its own follower counts */
  onFollowChange?: () => void;
}

export function OtherPetProfile({
  profileData,
  onBack,
  onMessage,
  onFollowChange,
}: OtherPetProfileProps) {
  const { t } = useTranslation();
  const { user: clerkUser } = useClerkUser();
  const { posts: allFeedPosts } = useSocialFeed();
  /** True when viewing a pet (not a user) — pets don't create posts, only get tagged */
  const isPetProfile = !!profileData.petId && !profileData.ownerId;
  const [activeTab, setActiveTab] = useState<'posts' | 'tagged'>(isPetProfile ? 'tagged' : 'posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followCheckDone, setFollowCheckDone] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [profilePosts, setProfilePosts] = useState<SocialPost[]>([]);
  const [taggedPosts, setTaggedPosts] = useState<SocialPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [taggedLoading, setTaggedLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);

  // Reset tab when switching between pet/user profiles
  useEffect(() => {
    setActiveTab(isPetProfile ? 'tagged' : 'posts');
  }, [isPetProfile]);

  // Trending score: compute from same global feed posts as ExplorePage for consistency
  const computedTrendingScore = useMemo(() => {
    if (!isPetProfile || !profileData.petId) return 0;
    const now = Date.now();
    let total = 0;
    for (const post of allFeedPosts) {
      const tagged = post.taggedPets ?? [];
      const isTagged = tagged.some((p) => p.id === profileData.petId) ||
        (post.taggedPetIds ?? []).includes(profileData.petId!);
      if (!isTagged) continue;
      const likes = post.likes ?? 0;
      const comments = post.comments ?? [];
      const createdAtMs = typeof post.createdAt === 'number' ? post.createdAt : null;
      const ageHours = createdAtMs != null ? Math.max(0, (now - createdAtMs) / 3_600_000) : 72;
      const quality = likes * 3 + comments.length * 5;
      const velocity = ageHours < 24 ? 2.0 : ageHours < 48 ? 1.5 : 1.0;
      const recency = Math.max(0, 48 - ageHours);
      const unique = Math.min(comments.length * 2, 20);
      const credibility = post.verified ? 15 : 0;
      const discussion = comments.length > 3 ? 10 : 0;
      const media = (post.images?.length ?? 0) > 0 ? 10 : 0;
      const score = quality * velocity + recency + unique + credibility + discussion + media;
      total += isNaN(score) ? 0 : score;
    }
    return Math.round(total);
  }, [isPetProfile, profileData.petId, allFeedPosts]);

  const headerAvatar = profileData.ownerAvatar ?? profileData.petAvatar;
  const mockProfile = {
    petName: profileData.petName,
    ownerName: profileData.ownerName,
    petAvatar: headerAvatar,
    professionalBadge: profileData.professionalBadge,
    species: 'Pet',
    breed: '—',
    get followers() {
      return followersCount;
    },
    get following() {
      return followingCount;
    },
    get posts() {
      return isPetProfile
        ? taggedPosts.length
        : profilePosts.filter((p) => (p.images?.length ?? 0) > 0).length;
    },
    bio: '',
    location: '',
  };

  // Resolve follow target: use PET type for pet-only profiles, USER for owner profiles
  const followTargetType: socialApi.FollowingType = isPetProfile
    ? 'PET'
    : profileData.ownerId
      ? 'USER'
      : profileData.petId
        ? 'PET'
        : 'USER';
  const followTargetId = isPetProfile
    ? profileData.petId!
    : profileData.ownerId || profileData.petId || '';

  /** True when the viewer is the same person as this profile — never show the Follow button. */
  const isSelf =
    !!clerkUser?.id &&
    (clerkUser.id === followTargetId || clerkUser.id === profileData.ownerId);

  const refreshCounts = useCallback(() => {
    if (!followTargetId) return;
    Promise.all([
      socialApi.getFollowers({ id: followTargetId, type: followTargetType }),
      socialApi.getFollowing({ id: followTargetId }),
    ])
      .then(([fr, fg]) => {
        setFollowersCount(fr.total);
        setFollowingCount(fg.total);
      })
      .catch(() => {});
  }, [followTargetId, followTargetType]);

  useEffect(() => {
    if (!followTargetId) {
      setFollowCheckDone(true);
      return;
    }
    let cancelled = false;
    socialApi
      .checkFollowStatus(followTargetType, followTargetId)
      .then(({ following }) => {
        if (!cancelled) {
          setIsFollowing(following);
          setFollowCheckDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFollowCheckDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [followTargetType, followTargetId]);

  useEffect(() => {
    if (!followTargetId) return;
    refreshCounts();
  }, [followTargetId, refreshCounts]);

  // Fetch posts by this user (for Posts tab). Public or visible when viewer follows.
  useEffect(() => {
    if (!profileData.ownerId) {
      setProfilePosts([]);
      return;
    }
    let cancelled = false;
    setPostsLoading(true);
    socialApi
      .getFeed({ ownerId: profileData.ownerId, limit: 50 })
      .then(({ posts }) => {
        if (!cancelled) setProfilePosts(posts);
      })
      .catch(() => {
        if (!cancelled) setProfilePosts([]);
      })
      .finally(() => {
        if (!cancelled) setPostsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileData.ownerId]);

  // Fetch posts where this pet is tagged (for Tagged tab).
  useEffect(() => {
    if (!profileData.petId) {
      setTaggedPosts([]);
      return;
    }
    let cancelled = false;
    setTaggedLoading(true);
    socialApi
      .getFeed({ taggedPetId: profileData.petId, limit: 50 })
      .then(({ posts }) => {
        if (!cancelled) setTaggedPosts(posts);
      })
      .catch(() => {
        if (!cancelled) setTaggedPosts([]);
      })
      .finally(() => {
        if (!cancelled) setTaggedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileData.petId]);

  const handleFollow = async () => {
    if (!followTargetId || isSelf) {
      toast.error(t('social.toast.cannotFollow'));
      return;
    }
    if (isFollowing) {
      setShowUnfollowConfirm(true);
      return;
    }
    setFollowLoading(true);
    try {
      await socialApi.follow({
        followingId: followTargetId,
        followingType: followTargetType,
      });
      setIsFollowing(true);
      refreshCounts();
      onFollowChange?.();
      toast.success(
        t('social.followingName', {
          name: mockProfile.ownerName || mockProfile.petName,
        })
      );
    } catch {
      toast.error(t('social.toast.followFailed'));
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollowConfirm = async () => {
    setShowUnfollowConfirm(false);
    if (!followTargetId) return;
    setFollowLoading(true);
    try {
      await socialApi.unfollow(followTargetType, followTargetId);
      setIsFollowing(false);
      refreshCounts();
      onFollowChange?.();
      toast.success(
        t('social.unfollowedName', {
          name: mockProfile.ownerName || mockProfile.petName,
        })
      );
    } catch {
      toast.error(t('social.toast.unfollowFailed'));
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMessage = () => {
    onMessage(followTargetId, mockProfile.ownerName, headerAvatar);
  };

  const updatePostInLists = useCallback(
    (postId: string, updater: (p: SocialPost) => SocialPost) => {
      setProfilePosts((prev) =>
        prev.map((p) => (p.id === postId ? updater(p) : p))
      );
      setTaggedPosts((prev) =>
        prev.map((p) => (p.id === postId ? updater(p) : p))
      );
    },
    []
  );

  const handleLike = useCallback(
    async (postId: string) => {
      const post =
        profilePosts.find((p) => p.id === postId) ??
        taggedPosts.find((p) => p.id === postId);
      if (!post) return;
      try {
        if (post.isLiked) {
          const { likes } = await socialApi.unlikePost(postId);
          updatePostInLists(postId, (p) => ({ ...p, isLiked: false, likes }));
          setSelectedPost((prev) =>
            prev?.id === postId ? { ...prev, isLiked: false, likes } : prev
          );
        } else {
          const { likes } = await socialApi.likePost(postId);
          updatePostInLists(postId, (p) => ({ ...p, isLiked: true, likes }));
          setSelectedPost((prev) =>
            prev?.id === postId ? { ...prev, isLiked: true, likes } : prev
          );
        }
      } catch {
        toast.error(t('common.error'));
      }
    },
    [profilePosts, taggedPosts, updatePostInLists]
  );

  const handleAddComment = useCallback(
    async (postId: string, text: string) => {
      try {
        const { comment } = await socialApi.addComment(postId, text);
        updatePostInLists(postId, (p) => ({
          ...p,
          comments: [...(p.comments ?? []), comment],
        }));
        setSelectedPost((prev) =>
          prev?.id === postId
            ? {
                ...prev,
                comments: [...(prev.comments ?? []), comment],
              }
            : prev
        );
      } catch {
        toast.error(t('social.failedAddComment'));
      }
    },
    [updatePostInLists]
  );

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 mb-6 px-4 py-2 rounded-xl text-[#464452] dark:text-white/70 hover:bg-white dark:hover:bg-white/5 transition-colors"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <ArrowLeft className="w-5 h-5" />
          {t('social.backToFeed')}
        </button>

        {/* Profile Header – responsive padding and layout for mobile */}
        <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border border-[#E2DFEA] dark:border-white/8 min-w-0">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 md:gap-8 mb-4 sm:mb-6 min-w-0">
            {/* Profile Picture – smaller on mobile */}
            <AvatarDisplay
              src={mockProfile.petAvatar}
              alt={mockProfile.petName}
              fallback="👤"
              className="text-4xl sm:text-6xl"
              containerClassName="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 flex-shrink-0"
            />

            {/* Profile Info – full width on mobile so stats can wrap */}
            <div className="flex-1 w-full min-w-0 text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <h1
                    className="text-2xl text-[#2D2F7E] dark:text-white"
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {mockProfile.ownerName || mockProfile.petName}
                  </h1>
                  <CheckCircle className="w-6 h-6 text-[#936FEF] fill-[#936FEF]" />
                </div>
                {followTargetId && followCheckDone && !isSelf && (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`px-6 py-2 rounded-xl transition-all flex items-center gap-2 disabled:opacity-70 ${
                      isFollowing
                        ? 'bg-[#F0EFF5] dark:bg-white/10 text-[#464452] dark:text-white/90 hover:bg-[#F0EFF5] dark:hover:bg-white/15'
                        : 'text-white hover:opacity-90'
                    }`}
                    style={
                      !isFollowing
                        ? {
                            background:
                              'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 700,
                          }
                        : { fontFamily: 'Sora, sans-serif', fontWeight: 700 }
                    }
                  >
                    {isFollowing ? (
                      <>
                        <UserCheck className="w-4 h-4" />
                        {t('social.following')}
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        {t('social.profile.follow')}
                      </>
                    )}
                  </button>
                )}
                {showUnfollowConfirm && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 max-w-sm w-full border dark:border-white/8">
                      <p
                        className="text-[#2D2F7E] dark:text-white font-medium mb-4"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('social.profile.unfollowConfirm', {
                          name: mockProfile.ownerName || mockProfile.petName,
                        })}
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleUnfollowConfirm}
                          className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700"
                        >
                          {t('social.profile.unfollow')}
                        </button>
                        <button
                          onClick={() => setShowUnfollowConfirm(false)}
                          className="flex-1 px-4 py-2 rounded-xl bg-[#F0EFF5] dark:bg-white/10 text-[#464452] dark:text-white font-semibold"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleMessage}
                  className="px-6 py-2 rounded-xl bg-[#F0EFF5] dark:bg-white/10 text-[#464452] dark:text-white/90 hover:bg-[#F0EFF5] dark:hover:bg-white/15 transition-colors flex items-center gap-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <MessageCircle className="w-4 h-4" />
                  {t('social.profile.message')}
                </button>
              </div>

              {/* Stats – flex-wrap + whitespace-nowrap so "Following" is never truncated on mobile */}
              <div
                className="flex flex-wrap gap-4 sm:gap-6 md:gap-8 mb-4 justify-center sm:justify-start min-w-0"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <div className="whitespace-nowrap">
                  <span
                    className="font-bold text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {mockProfile.posts}
                  </span>
                  <span className="ml-1 text-[#464452] dark:text-white/70">
                    {isPetProfile ? t('social.tagged') : t('social.posts')}
                  </span>
                </div>
                <button className="hover:opacity-80 whitespace-nowrap touch-manipulation">
                  <span
                    className="font-bold text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {mockProfile.followers.toLocaleString()}
                  </span>
                  <span className="ml-1 text-[#464452] dark:text-white/70">
                    {t('social.followers')}
                  </span>
                </button>
                {isPetProfile ? (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <TrendingUp className="w-4 h-4 text-[#936FEF]" />
                    <span
                      className="font-bold text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {computedTrendingScore.toLocaleString()}
                    </span>
                    <span className="ml-1 text-[#464452] dark:text-white/70">
                      Trending Score
                    </span>
                  </div>
                ) : (
                  <button className="hover:opacity-80 whitespace-nowrap touch-manipulation">
                    <span
                      className="font-bold text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {mockProfile.following.toLocaleString()}
                    </span>
                    <span className="ml-1 text-[#464452] dark:text-white/70">
                      {t('social.following')}
                    </span>
                  </button>
                )}
              </div>

              {/* Bio */}
              <div className="mb-4">
                {mockProfile.professionalBadge && (
                  <p
                    className="text-[#936FEF] dark:text-purple-300 mb-1"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {mockProfile.professionalBadge}
                  </p>
                )}
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {mockProfile.bio || t('social.profile.noBioYet')}
                </p>
              </div>

              {/* Professional Badge */}
              {mockProfile.professionalBadge && (
                <span
                  className="inline-block px-3 py-1 rounded-full text-sm bg-[#F0EFF5] dark:bg-purple-900/20 text-[#936FEF]"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {mockProfile.professionalBadge}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs – pet profiles only show Tagged; user profiles show Posts + Tagged */}
        <div className="bg-white dark:bg-[#16151D] rounded-t-2xl border-t border-l border-r border-[#E2DFEA] dark:border-white/8 min-w-0">
          <div className="flex border-b border-[#E2DFEA] dark:border-white/8 min-w-0">
            {!isPetProfile && (
              <button
                onClick={() => setActiveTab('posts')}
                className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 transition-colors relative min-w-0 px-2 touch-manipulation ${
                  activeTab === 'posts'
                    ? 'text-[#2D2F7E] dark:text-white'
                    : 'text-[#464452] dark:text-white/70'
                }`}
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
              >
                <Grid className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span className="truncate">
                  {t('social.posts').toUpperCase()}
                </span>
                {activeTab === 'posts' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2D2F7E] dark:bg-white" />
                )}
              </button>
            )}
            <button
              onClick={() => setActiveTab('tagged')}
              className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 transition-colors relative min-w-0 px-2 touch-manipulation ${
                activeTab === 'tagged'
                  ? 'text-[#2D2F7E] dark:text-white'
                  : 'text-[#464452] dark:text-white/70'
              }`}
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="truncate">
                {t('social.tagged').toUpperCase()}
              </span>
              {activeTab === 'tagged' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2D2F7E] dark:bg-white" />
              )}
            </button>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="bg-white dark:bg-[#16151D] rounded-b-2xl p-3 sm:p-6 border-b border-l border-r border-[#E2DFEA] dark:border-white/8 min-w-0">
          {activeTab === 'posts' &&
            (postsLoading ? (
              <div className="text-center py-12">
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('common.loading')}
                </p>
              </div>
            ) : profilePosts.filter((p) => (p.images?.length ?? 0) > 0)
                .length === 0 ? (
              <div className="text-center py-12">
                <Grid className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.noPostsYet')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1 sm:gap-2 md:gap-4">
                {profilePosts
                  .filter((p) => (p.images?.length ?? 0) > 0)
                  .map((post) => (
                    <button
                      key={post.id}
                      className="relative aspect-square rounded-xl overflow-hidden group"
                      onClick={() => setSelectedPost(post)}
                    >
                      <SocialPostImage
                        src={post.images?.[0]}
                        alt={`Post ${post.id}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
                        <div className="flex items-center gap-2 text-white">
                          <Heart className="w-6 h-6 fill-white" />
                          <span
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {post.likes}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <MessageCircle className="w-6 h-6 fill-white" />
                          <span
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {post.comments?.length ?? 0}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            ))}
          {activeTab === 'tagged' &&
            (taggedLoading ? (
              <div className="text-center py-12">
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('common.loading')}
                </p>
              </div>
            ) : taggedPosts.filter((p) => (p.images?.length ?? 0) > 0)
                .length === 0 ? (
              <div className="text-center py-12">
                <Tag className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-white/20" />
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {isPetProfile
                    ? t('social.noPetTaggedYet', 'No posts featuring this pet yet.')
                    : t('social.noTaggedYet')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1 sm:gap-2 md:gap-4">
                {taggedPosts
                  .filter((p) => (p.images?.length ?? 0) > 0)
                  .map((post) => (
                    <button
                      key={post.id}
                      className="relative aspect-square rounded-xl overflow-hidden group"
                      onClick={() => setSelectedPost(post)}
                    >
                      <SocialPostImage
                        src={post.images?.[0]}
                        alt={`Post ${post.id}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
                        <div className="flex items-center gap-2 text-white">
                          <Heart className="w-6 h-6 fill-white" />
                          <span
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {post.likes}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <MessageCircle className="w-6 h-6 fill-white" />
                          <span
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {post.comments?.length ?? 0}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            ))}
        </div>
      </div>

      {/* Post detail popup (Instagram-style card) */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onLike={handleLike}
          onAddComment={handleAddComment}
        />
      )}
    </div>
  );
}
